
import { Button } from "@/components/ui/button";
import { Check, LogOut, Lock } from "lucide-react";
import { Profile } from "@/hooks/profile/types";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MembershipBadge } from "./profile/MembershipBadge";
import { ProfileDetails } from "./profile/ProfileDetails";
import { PasswordChangeForm } from "./profile/PasswordChangeForm";
import { EmailConfirmationAlert } from "./profile/EmailConfirmationAlert";
import { useEmailConfirmationStatus } from "@/hooks/profile/useEmailConfirmationStatus";

interface ProfileFormProps {
  profile: Profile;
  onUpdate: (profile: Partial<Profile>) => Promise<void>;
  onLogout: () => Promise<void>;
}

export const ProfileForm = ({ profile, onUpdate, onLogout }: ProfileFormProps) => {
  const [searchParams] = useSearchParams();
  const [showPasswordChange, setShowPasswordChange] = useState(
    searchParams.get('action') === 'change_password'
  );
  const [birthdate, setBirthdate] = useState(profile.birthdate || "");
  const [email, setEmail] = useState(profile.username || "");
  const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url || "");
  const [dni, setDni] = useState(profile.dni || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isPendingConfirmation } = useEmailConfirmationStatus();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.currentTarget);
      
      await onUpdate({
        username: email,
        first_name: formData.get('firstName') as string,
        last_name: formData.get('lastName') as string,
        birthdate: birthdate || null,
        dni: (formData.get('dni') as string) || null,
        phone: (formData.get('phone') as string) || null,
        address: (formData.get('address') as string) || null,
        emergency_phone: (formData.get('emergencyPhone') as string) || null,
        health_insurance: (formData.get('healthInsurance') as string) || null,
      });
    } catch (error) {
      console.error("Error al actualizar perfil:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBirthdateChange = (newBirthdate: string) => {
    setBirthdate(newBirthdate);
  };

  const handleEmailChange = (newEmail: string) => {
    setEmail(newEmail);
  };

  const handleAvatarChange = async (newAvatarUrl: string) => {
    setAvatarUrl(newAvatarUrl);
    await onUpdate({ avatar_url: newAvatarUrl });
  };

  return (
    <div className="space-y-6 profile-section">
      {isPendingConfirmation && <EmailConfirmationAlert />}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Membership Badge */}
        <MembershipBadge role={profile.role} />
        
        {/* Profile Details */}
        <ProfileDetails 
          profile={{...profile, birthdate, username: email, avatar_url: avatarUrl, dni}} 
          onBirthdateChange={handleBirthdateChange}
          onEmailChange={handleEmailChange}
          onAvatarChange={handleAvatarChange}
        />
        
        {/* Save Button */}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-11 bg-primary hover:bg-primary/90 transition-all shadow-sm"
        >
          <Check className="mr-2 h-4 w-4" />
          {isSubmitting ? "Guardando..." : "Guardar cambios"}
        </Button>
      </form>

      {/* Security Section */}
      <div className="space-y-4 p-4 rounded-xl bg-card/30 border border-border/50">
        <h4 className="text-sm font-medium text-muted-foreground">Seguridad</h4>
        
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowPasswordChange(!showPasswordChange)}
          className="w-full justify-start cambiocontraseña bg-background/50 hover:bg-background"
        >
          <Lock className="mr-2 h-4 w-4" />
          {showPasswordChange ? "Cancelar cambio de contraseña" : "Cambiar contraseña"}
        </Button>

        {showPasswordChange && (
          <div className="pt-2">
            <PasswordChangeForm onCancel={() => setShowPasswordChange(false)} />
          </div>
        )}
      </div>

      {/* Logout Button */}
      <Button
        type="button"
        onClick={onLogout}
        variant="ghost"
        className="w-full text-destructive hover:text-destructive hover:bg-destructive/10 transition-colors"
      >
        <LogOut className="mr-2 h-4 w-4" />
        Cerrar sesión
      </Button>
    </div>
  );
};
